// Admin v2 theme — light + dark via a tiny localStorage-backed context.
// Components opt into theme by reading `useAdminTheme()` and using the `cls`
// helpers below, which pick the right Tailwind classes for the active theme.
import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'voipinger.admin-v2.theme';

const AdminThemeContext = createContext({ theme: 'dark', toggle: () => {} });

export function AdminThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch (_) {}
    return 'dark';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {}
  }, [theme]);

  const toggle = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  return (
    <AdminThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </AdminThemeContext.Provider>
  );
}

export function useAdminTheme() {
  return useContext(AdminThemeContext);
}

// Class helpers — `t` is 'light' | 'dark'. Each helper returns the right
// Tailwind classes for the surface it represents. Keeping these here makes
// the admin look consistent across screens without dozens of conditionals.
export const tc = {
  pageBg: (t) => (t === 'light' ? 'bg-slate-50' : 'bg-slate-950'),
  sidebarBg: (t) => (t === 'light' ? 'bg-white border-r border-slate-200' : 'bg-slate-900/85 border-r border-white/10 backdrop-blur-xl'),
  headerBg: (t) => (t === 'light' ? 'bg-white border-b border-slate-200' : 'bg-slate-900/40 border-b border-white/10 backdrop-blur'),
  cardBg: (t) => (t === 'light' ? 'bg-white border border-slate-200' : 'bg-slate-900/60 border border-white/10'),
  cardMutedBg: (t) => (t === 'light' ? 'bg-slate-100 border border-slate-200' : 'bg-slate-950/40 border border-white/10'),
  inputBg: (t) => (t === 'light' ? 'bg-white border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 text-slate-900 placeholder:text-slate-400' : 'bg-slate-900/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/20 text-slate-100 placeholder:text-slate-600'),
  textPrimary: (t) => (t === 'light' ? 'text-slate-900' : 'text-slate-100'),
  textMuted: (t) => (t === 'light' ? 'text-slate-600' : 'text-slate-400'),
  textDim: (t) => (t === 'light' ? 'text-slate-500' : 'text-slate-500'),
  hoverBg: (t) => (t === 'light' ? 'hover:bg-slate-100' : 'hover:bg-white/5'),
  divider: (t) => (t === 'light' ? 'border-slate-200' : 'border-white/10'),
  navActiveBg: (t) => (t === 'light' ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-gradient-to-r from-violet-500/25 to-fuchsia-500/25 text-white border border-violet-500/50 shadow-lg shadow-violet-500/15'),
  navIdle: (t) => (t === 'light' ? 'text-slate-600 hover:text-slate-900 border border-transparent' : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'),
};
